import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FaqItem from "@/pages/help/components/FaqItem";
import { usePageMeta } from "@/hooks/usePageMeta";

const faqs = [
  {
    question: "如何查找感兴趣的直播？",
    answer:
      "你可以通过顶部搜索框输入直播标题或主播昵称，也可以从首页的分类快捷入口进入对应的直播分类页，再通过地区、直播状态和主播等级等条件进行筛选和排序。",
  },
  {
    question: "搜索结果为空怎么办？",
    answer:
      "可以尝试更换更简短的关键词，或点击“重置筛选”按钮清除筛选条件。我们也会在无结果时为你推荐热门直播，方便继续浏览。",
  },
  {
    question: "直播为什么显示“演示效果，待后端接口接入”？",
    answer:
      "本站为前端演示站点，播放器与弹幕均为效果演示，暂未接入真实直播流与弹幕服务器。接入真实接口后即可正常观看与互动。",
  },
  {
    question: "弹幕和点赞会被保存吗？",
    answer:
      "不会。弹幕滚动为固定动画模拟，你发送的弹幕和点赞仅在本页面临时展示，刷新页面后不会保留，也不会上传到任何服务器。",
  },
  {
    question: "如何分享某个直播间？",
    answer:
      "进入直播详情页后，点击互动面板中的“分享”按钮，系统会自动生成并复制该直播的链接，你可以粘贴发送给朋友。",
  },
  {
    question: "需要登录才能观看吗？",
    answer:
      "不需要。蜜梨直播演示站点无需登录即可浏览和观看，交互功能如弹幕、点赞等均可直接体验。",
  },
];

const watchTips = [
  { icon: "ri-wifi-line", text: "建议在网络稳定的 Wi-Fi 环境下观看，以获得更清晰流畅的画面。" },
  { icon: "ri-volume-up-line", text: "部分直播以声音内容为主，建议佩戴耳机获得更好体验。" },
  { icon: "ri-eye-line", text: "观看时注意适度休息，长时间观看注意用眼健康。" },
  { icon: "ri-chat-smile-2-line", text: "参与互动请文明发言，共同维护友善的直播氛围。" },
];

const techIssues = [
  { icon: "ri-refresh-line", title: "画面卡顿", desc: "尝试刷新页面或切换网络环境，也可稍后重试。" },
  { icon: "ri-image-line", title: "封面未加载", desc: "检查网络连接，稍等片刻后图片会自动加载完成。" },
  { icon: "ri-mic-off-line", title: "没有声音", desc: "确认设备未静音，并检查浏览器标签页是否被静音。" },
  { icon: "ri-smartphone-line", title: "手机端显示异常", desc: "建议使用最新版浏览器，或横屏/竖屏切换后重新查看。" },
];

export default function Help() {
  usePageMeta({
    title: "用户互动帮助 - 蜜梨直播",
    description:
      "蜜梨直播用户互动帮助中心，汇总常见问题解答、观看直播注意事项、常见技术问题指导与反馈渠道。",
    keywords: "蜜梨直播帮助,直播常见问题,观看技巧,用户反馈",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="w-full flex-1 px-4 py-6 md:px-8">
        <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-semibold text-primary-700">
            <i className="ri-customer-service-2-line text-base" />
            帮助中心
          </span>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            用户互动帮助
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里汇总了观看直播与参与互动时最常见的问题与操作指引。如果没有找到答案，欢迎通过页面底部的联系方式与我们联系。
          </p>
        </section>

        <section className="mt-8">
          <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">常见问题解答</h2>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {faqs.map((faq) => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">观看直播注意事项</h2>
            <ul className="mt-4 space-y-3">
              {watchTips.map((tip) => (
                <li
                  key={tip.text}
                  className="flex items-start gap-3 rounded-md bg-secondary-100 px-4 py-3 text-sm leading-relaxed text-foreground-700"
                >
                  <i className={`${tip.icon} mt-0.5 text-base text-primary-500`} />
                  {tip.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">常见技术问题指导</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {techIssues.map((issue) => (
                <div key={issue.title} className="rounded-md bg-background-100 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${issue.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 text-sm font-bold text-foreground-950">{issue.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{issue.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">用户反馈渠道说明</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            如果你在使用过程中遇到问题，或对平台有任何建议，可以通过以下渠道反馈。我们会在客服在线时间内尽快回复。
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-md bg-secondary-100 p-4">
              <i className="ri-mail-line text-xl text-primary-500" />
              <h3 className="mt-2 text-sm font-bold text-foreground-950">邮件支持</h3>
              <p className="mt-1 text-xs text-foreground-600">support@mili-live.example.com</p>
            </div>
            <div className="rounded-md bg-secondary-100 p-4">
              <i className="ri-feedback-line text-xl text-primary-500" />
              <h3 className="mt-2 text-sm font-bold text-foreground-950">意见反馈</h3>
              <p className="mt-1 text-xs text-foreground-600">feedback@mili-live.example.com</p>
            </div>
            <div className="rounded-md bg-secondary-100 p-4">
              <i className="ri-time-line text-xl text-primary-500" />
              <h3 className="mt-2 text-sm font-bold text-foreground-950">服务时间</h3>
              <p className="mt-1 text-xs text-foreground-600">每日 09:00 - 23:00</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/about"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-information-line text-base" />
              了解关于我们
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line text-base" />
              返回首页
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}